import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAppSelector } from '../../../../hook/useAppSelector';
import { numberFormatter } from '../../../../util/numberFormatter';
import { t } from 'i18next';
import { StatsTableColumn } from '../../../../enum/TableColumn';

/*
  TableCellWithTooltip component

  This component renders a table cell or an icon with an optional tooltip.
  The tooltip is displayed when:
    1. For table cells: the content overflows the element width (scrollWidth > clientWidth) or it is a header cell.
    2. For image columns: when the user hovers over the icon.

  The tooltip is rendered using React Portal, meaning it is displayed
  outside of the normal DOM flow and is not clipped by parent elements with overflow:hidden.

  Props interface:

    value?: string | number
      - The text or number displayed inside the table cell.
      - For table cells, the tooltip shows this content.

    rowDensityClass?: string
      - Optional CSS class to adjust row height or cell styling for different row densities.

    headerCell?: boolean
      - If true, the cell is treated as a header cell.
      - Tooltip automatically handles header overflow.

    className?: string
      - Additional CSS class for styling the cell or the icon.

    isImageColumn?: boolean
      - If true, the cell is treated as an image column (icon).
      - Tooltip is shown when hovering over the icon.

    icon?: string
      - The path to the icon image, used only if isImageColumn is true.

    text?: string
      - The text displayed inside the tooltip for the icon.

    onClick?: () => void
      - Callback function triggered when the user clicks the icon.

  Internal behavior:
    - Uses useRef to reference the DOM element for tooltip positioning.
    - Uses useState to track tooltip visibility and overflow state.
    - useEffect with ResizeObserver detects when table cell content overflows.
    - Tooltip automatically hides on scroll or touch events.
    - Image column and table cell tooltips use different CSS classes and styles.
    - Tooltip positions above the cell or icon dynamically based on its bounding rectangle.
*/

interface TableCellWithTooltipProps {
  value?: string | number;
  rowDensityClass?: string;
  headerCell?: boolean;
  className?: string;
  column?: string;

  isImageColumn?: boolean;
  icon?: string;
  text?: string;
  onClick?: () => void;
}

const TableCellWithTooltip: React.FC<TableCellWithTooltipProps> = ({
  value,
  rowDensityClass,
  headerCell = false,
  className,
  column,
  isImageColumn = false,
  icon,
  text,
  onClick,
}) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);

  const format = useAppSelector((state) => state.configSlice.format);

  /* useEffect to check if the content overflows the cell width.
  If scrollWidth > clientWidth, then content is overflowing and tooltip should be enabled. */
  useEffect(() => {
    if (isImageColumn) return;

    const el = cellRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [isImageColumn]);

  const handleMouseEnter = () => {
    if (isOverflowed || isImageColumn) setShowTooltip(true);
  };
  const handleMouseLeave = () => setShowTooltip(false);

  const rect = cellRef.current?.getBoundingClientRect();

  /* useEffect to hide the tooltip when the user scrolls or touches anywhere outside.
  This prevents tooltip from staying visible when the user scrolls the table. */
  useEffect(() => {
    const handleScrollOrTouch = () => setShowTooltip(false);

    if (showTooltip) {
      window.addEventListener('scroll', handleScrollOrTouch, true);
      window.addEventListener('touchstart', handleScrollOrTouch, true);
    }
    return () => {
      window.removeEventListener('scroll', handleScrollOrTouch, true);
      window.removeEventListener('touchstart', handleScrollOrTouch, true);
    };
  }, [showTooltip]);

  return (
    <>
      {isImageColumn && icon && (
        <div
          ref={cellRef}
          className={`tooltip ${className}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={(e) => {
            e.stopPropagation();
            if (onClick) {
              onClick();
            }
          }}
        >
          <img src={icon} alt="" />
        </div>
      )}

      {!isImageColumn && !headerCell && (
        <div
          ref={cellRef}
          className={`table_cell ${rowDensityClass}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleMouseEnter}
        >
          {typeof value === 'number' ? (
            column === StatsTableColumn.PERCENTAGE && !isNaN(Number(value)) ? (
              `${numberFormatter(Number(value), format)} %`
            ) : (
              numberFormatter(value, format)
            )
          ) : value ? (
            value
          ) : (
            <span className="no_statistics">({t('statisticPage.table.noStatistics')})</span>
          )}
        </div>
      )}

      {!isImageColumn && headerCell && (
        <span
          ref={cellRef}
          className={className}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleMouseEnter}
        >
          {value}
        </span>
      )}

      {showTooltip &&
        rect &&
        createPortal(
          <span
            className={
              isImageColumn
                ? `tooltiptext ${className || ''} images_column`
                : `tooltip_portal ${headerCell ? 'tooltip_portal_header' : ''}`
            }
            style={{
              top: isImageColumn ? rect.top : rect.top - (headerCell ? 40 : 30),
              left: rect.left + rect.width / 2,
            }}
          >
            {isImageColumn ? text : value}
          </span>,
          document.body,
        )}
    </>
  );
};

export default TableCellWithTooltip;
